import cx from "classnames";

import { IPaginator } from "common/types";

import Pagination from "client/common/components/layouts/Pagination";
import { useBreakpoints } from "client/common/hooks";
import { ProductListSource } from "client/analytics/types";
import {
  ProductListContext,
  useProductsViewOnVisible,
  useViewItemListOnVisible,
} from "client/analytics/utils";

import { ProductSearchResultV2 } from "../types";
import ProductCardV2 from "./ProductCardV2";
import ProductCardMobileV2 from "./ProductCardMobileV2";

import styles from "../styles/ProductCardList.module.less";

type ProductCardListProps = {
  results: ProductSearchResultV2[];
  listSource: ProductListSource;
  paginator: IPaginator;
  onPageChange: (page: number) => void;
  entityName: string;
  listContext?: ProductListContext;
  className?: string;
  /** Set when the filter panel sits beside the grid; drops one column. */
  isNarrow?: boolean;
};

/**
 * A page of product cards and its pagination: a grid on desktop, a stacked list on mobile. Reports
 * its own impressions, so callers only supply the data and the list's analytics source.
 */
const ProductCardList = ({
  results,
  listSource,
  paginator,
  onPageChange,
  entityName,
  listContext,
  className,
  isNarrow = false,
}: ProductCardListProps) => {
  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const itemListRef = useViewItemListOnVisible(results, listSource);
  const productsViewRef = useProductsViewOnVisible(results, listSource, listContext);

  const renderCards = () => {
    if (isTabletPortraitAndSmaller) {
      return (
        <div ref={productsViewRef} className={styles.list}>
          {results.map((result) => (
            <ProductCardMobileV2
              key={result.id}
              result={result}
              listSource={listSource}
              collectionSlug={listContext?.collectionSlug}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={productsViewRef}
        className={cx(styles.grid, styles.gridV2, isNarrow && styles.gridNarrow)}
      >
        {results.map((result) => (
          <ProductCardV2
            key={result.id}
            result={result}
            listSource={listSource}
            collectionSlug={listContext?.collectionSlug}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={itemListRef} className={cx(styles.section, className)}>
      {renderCards()}
      <Pagination paginator={paginator} onPageChange={onPageChange} entityName={entityName} />
    </div>
  );
};

export default ProductCardList;
